import {
  EnvironmentProviders,
  provideCheckNoChangesConfig,
  provideZoneChangeDetection,
  provideZonelessChangeDetection,
} from '@angular/core';
import type { AppConfig } from '@core/services/config/app-config.service';

/**
 * Which change-detection strategy the application bootstraps with.
 *
 * Angular 22 defaults to zoneless; this app still opts out with
 * `provideZoneChangeDetection()`. AF2 Phase 5 wave 3a turns that opt-out into a
 * per-deployment choice so staging can soak zoneless on the same artefact
 * production runs. See
 * `docs/superpowers/plans/2026-08-11-af2-phase5-zoneless.md`.
 */
export type ChangeDetectionMode = 'zone' | 'zoneless';

/** The decision, separated from the Angular providers so it can be asserted directly. */
export interface ChangeDetectionPlan {
  readonly mode: ChangeDetectionMode;
  /**
   * Whether to register `provideCheckNoChangesConfig({ exhaustive: true })`. Angular strips
   * that provider entirely from optimized builds (it is `ngDevMode`-gated), so this is only
   * ever true for a development build — never for a production one.
   */
  readonly exhaustiveCheckNoChanges: boolean;
}

/** The slice of the deployed browser configuration this decision reads. */
export type ChangeDetectionConfig = Pick<AppConfig, 'zonelessChangeDetection'>;

/** The slice of the compile-time environment this decision reads. */
export interface ChangeDetectionBuildEnvironment {
  readonly production: boolean;
}

/**
 * Pure decision: zone-based unless the deployment explicitly opted into zoneless.
 *
 * The flag arrives as a real boolean because `parseFeatureFlags()` has already coerced the
 * envsubst string, but an absent or non-boolean value must still resolve to the zone default,
 * so this compares strictly rather than trusting truthiness.
 */
export function planChangeDetection(
  config: ChangeDetectionConfig | undefined,
  buildEnvironment: ChangeDetectionBuildEnvironment
): ChangeDetectionPlan {
  const zoneless = config?.zonelessChangeDetection === true;
  return {
    mode: zoneless ? 'zoneless' : 'zone',
    exhaustiveCheckNoChanges: zoneless && !buildEnvironment.production,
  };
}

/**
 * Application-wide change-detection providers for `bootstrapApplication`.
 *
 * `zone.js` stays in the polyfills while this switch exists, so a zoneless deployment logs
 * Angular's NG0914 warning ("zone.js is loaded but not used"). That is expected and harmless;
 * the polyfill leaves with the switch in wave 3b.
 */
export function provideChangeDetection(
  config: ChangeDetectionConfig | undefined,
  buildEnvironment: ChangeDetectionBuildEnvironment
): EnvironmentProviders[] {
  const plan = planChangeDetection(config, buildEnvironment);
  return [
    plan.mode === 'zoneless'
      ? provideZonelessChangeDetection()
      : provideZoneChangeDetection(),
    // Exhaustive checkNoChanges treats every view as Eager when verifying that nothing
    // changed after a check, which is what surfaces state written without scheduling a
    // render. Development builds only.
    ...(plan.exhaustiveCheckNoChanges
      ? [provideCheckNoChangesConfig({ exhaustive: true })]
      : []),
  ];
}
